import React, { useEffect, Suspense, lazy } from 'react';
import './i18n/i18n';
import { Routes, Route, Navigate } from 'react-router-dom';  // no BrowserRouter here
import { lazyWithRetry } from './utils/chunkErrorHandler';

// Remove parseAmplifyConfig - not needed in Amplify v6
import Navbar from './components/Navbar';
import CourseList from './components/CourseList';
import CourseDetail from './components/CourseDetail';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';

// TEMPORARY DEBUG - Remove after testing
import { debugBooking } from './utils/debugBooking';
import { quickStatusCheck } from './utils/quickStatusCheck';
import { testAdminSelfBooking } from './utils/testAdminSelfBooking';
import { compareUserViews } from './utils/compareUserViews';
import { checkAllCourses } from './utils/checkAllCourses';
import { directCourseCheck } from './utils/directCourseCheck';
import { testJulyBooking } from './utils/testJulyBooking';
import { diagnoseCourse } from './utils/diagnoseCourse';
import { quickCourseCheck } from './utils/quickCourseCheck';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from './hooks/useAuth';

// lazy-load dashboards with retry mechanism
const AdminDashboard = lazy(() => lazyWithRetry(() => import('./pages/AdminDashboard')));
const StudentDashboard = lazy(() => lazyWithRetry(() => import('./pages/StudentDashboard')));
const CollegeDashboard = lazy(() => lazyWithRetry(() => import('./pages/CollegeDashboard')));
const HouseDashboard = lazy(() => lazyWithRetry(() => import('./pages/HouseDashboard')));
const TeacherDashboard = lazy(() => lazyWithRetry(() => import('./pages/TeacherDashboard')));
const ParentDashboard = lazy(() => lazyWithRetry(() => import('./pages/ParentDashboard')));
const InformationPage = lazy(() => lazyWithRetry(() => import('./pages/InformationPage')));
const LandingPage = lazy(() => lazyWithRetry(() => import('./pages/LandingPage')));
const TeacherAssignmentsPage = lazy(() => lazyWithRetry(() => import('./pages/TeacherAssignmentsPage')));
const PrincipalAssignmentsPage = lazy(() => lazyWithRetry(() => import('./pages/PrincipalAssignmentsPage')));
const StaffDashboard = lazy(() => lazyWithRetry(() => import('./pages/StaffDashboard')));
const StaffProfilePage = lazy(() => lazyWithRetry(() => import('./pages/StaffProfilePage')));
const StaffRolesPage = lazy(() => lazyWithRetry(() => import('./pages/admin/StaffRolesPage')));
const AccessRequestsReviewPage = lazy(() => lazyWithRetry(() => import('./pages/admin/AccessRequestsReviewPage')));
const ComprehensiveCourseReportPage = lazy(() => lazyWithRetry(() => import('./pages/ComprehensiveCourseReportPage')));
const AdminFormsPage = lazy(() => lazyWithRetry(() => import('./pages/AdminFormsPage')));
const AdminPaymentTypesPage = lazy(() => lazyWithRetry(() => import('./pages/AdminPaymentTypesPage')));
const AdminPaymentsPage = lazy(() => lazyWithRetry(() => import('./pages/AdminPaymentsPage')));
const AdminGlobalPayPage = lazy(() => lazyWithRetry(() => import('./pages/AdminGlobalPayPage')));
const AdminBookingsPage = lazy(() => lazyWithRetry(() => import('./pages/AdminBookingsPage')));
const BookingDetailsPage = lazy(() => lazyWithRetry(() => import('./pages/BookingDetailsPage')));
const AuditCleanBookingsPage = lazy(() => lazyWithRetry(() => import('./pages/AuditCleanBookingsPage')));
const AdminAuditLogPage = lazy(() => lazyWithRetry(() => import('./pages/AdminAuditLogPage')));
const AdminPermissionsPage = lazy(() => lazyWithRetry(() => import('./pages/AdminPermissionsPage')));
const AdminReportsPage = lazy(() => lazyWithRetry(() => import('./pages/AdminReportsPage')));
const AdminHousesPage = lazy(() => lazyWithRetry(() => import('./pages/AdminHousesPage')));
const AdminCourseHousesPage = lazy(() => lazyWithRetry(() => import('./pages/AdminCourseHousesPage')));
const HousingDashboard = lazy(() => lazyWithRetry(() => import('./components/admin/HousingDashboard')));
const CourseManagementPage = lazy(() => lazyWithRetry(() => import('./pages/CourseManagementPage')));
const CourseDetailsManagementPage = lazy(() => lazyWithRetry(() => import('./pages/CourseDetailsManagementPage')));
const StudentCourseDetailsPage = lazy(() => lazyWithRetry(() => import('./pages/StudentCourseDetailsPage')));
const CourseBookPage = lazy(() => lazyWithRetry(() => import('./pages/CourseBookPage')));
const AdminStudentsPage = lazy(() => lazyWithRetry(() => import('./pages/AdminStudentsPage')));
const StudentDetailsPage = lazy(() => lazyWithRetry(() => import('./pages/StudentDetailsPage')));
const EditBookingFormPage = lazy(() => lazyWithRetry(() => import('./pages/EditBookingFormPage')));
const AdminWaitlistPage = lazy(() => lazyWithRetry(() => import('./pages/AdminWaitlistPage')));
const AdminRefundPage = lazy(() => lazyWithRetry(() => import('./pages/AdminRefundPage')));
const AdminSlotQueuePage = lazy(() => lazyWithRetry(() => import('./pages/AdminSlotQueuePage')));
const AdminCourseSlotStatusPage = lazy(() => lazyWithRetry(() => import('./pages/AdminCourseSlotStatusPage')));
const AdminSlotMonitoringPage = lazy(() => lazyWithRetry(() => import('./pages/AdminSlotMonitoringPage')));
const AdminEmailPage = lazy(() => lazyWithRetry(() => import('./pages/AdminEmailPage')));
const AdminEmailDiagnosticPage = lazy(() => lazyWithRetry(() => import('./pages/AdminEmailDiagnosticPage')));
const DataManagementPage = lazy(() => lazyWithRetry(() => import('./pages/DataManagementPage')));
const AdminManagementPage = lazy(() => lazyWithRetry(() => import('./pages/admin/AdminManagementPage')));
const StudentProfilePage = lazy(() => lazyWithRetry(() => import('./pages/student/StudentProfilePage')));
const GuardianStudentProfilePage = lazy(() => lazyWithRetry(() => import('./pages/guardian/GuardianStudentProfilePage')));
const GuardianProfilePage = lazy(() => lazyWithRetry(() => import('./pages/guardian/GuardianProfilePage')));
const FinancialDashboard = lazy(() => lazyWithRetry(() => import('./pages/FinancialDashboard')));
const FinancialDiagnosticsPage = lazy(() => lazyWithRetry(() => import('./pages/FinancialDiagnosticsPage')));
const PrintableStudentRecordPage = lazy(() => lazyWithRetry(() => import('./pages/PrintableStudentRecordPage')));
const GlobalPaymentsPage = lazy(() => lazyWithRetry(() => import('./pages/GlobalPaymentsPage')));
const AdminPaymentFixPage = lazy(() => lazyWithRetry(() => import('./pages/AdminPaymentFixPage')));
const AdminCognitoUsersPage = lazy(() => lazyWithRetry(() => import('./pages/AdminCognitoUsersPage')));
const AdminGuardianInterestPage = lazy(() => lazyWithRetry(() => import('./pages/AdminGuardianInterestPage')));
const AdminSearchPage = lazy(() => lazyWithRetry(() => import('./pages/AdminSearchPage')));
const UserViewAsPage = lazy(() => lazyWithRetry(() => import('./pages/UserViewAsPage')));
const AdminHomeDashboard = lazy(() => lazyWithRetry(() => import('./pages/AdminHomeDashboard')));
const IssueListPage = lazy(() => lazyWithRetry(() => import('./pages/admin/IssueListPage')));
const IssueSubmissionPage = lazy(() => lazyWithRetry(() => import('./pages/admin/IssueSubmissionPage')));
const IssueEditPage = lazy(() => lazyWithRetry(() => import('./pages/admin/IssueEditPage')));
const AdminFormManagementPage = lazy(() => lazyWithRetry(() => import('./pages/AdminFormManagementPage')));
const WaitlistAcceptPage = lazy(() => lazyWithRetry(() => import('./pages/WaitlistAcceptPage')));

const Spinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-blue-500 border-gray-200 dark:border-gray-700" />
    <span className="sr-only">Loading...</span>
  </div>
);

function RequireAuth({ children, requiredGroups = [] }) {
  const { user, groups } = useAuth();

  // DEBUG: Log auth check details for troubleshooting
  console.log('🔐 RequireAuth check:', {
    userEmail: user?.signInDetails?.loginId || user?.username,
    groups: groups,
    requiredGroups: requiredGroups,
    hasRequiredGroup: requiredGroups.length === 0 || requiredGroups.some(g => groups.includes(g))
  });

  if (!user) return <Navigate to="/login" replace />;
  if (requiredGroups.length && !requiredGroups.some(g => groups.includes(g))) {
    console.log('❌ Access denied - user groups:', groups, 'required:', requiredGroups);
    return <Navigate to="/unauthorized" replace />;
  }
  return children;
}

export default function App() {
  const { user, groups, loading } = useAuth();

  useEffect(() => {
    console.debug('Auth state:', { user, groups });
    
    // DEBUG disabled - causing 404 errors
    // if (user && window.debugBooking) {
    //   window.debugBooking();
    // }
  }, [user, groups]);

  // Wait for auth to load
  if (loading) return <Spinner />;

  const Dashboard = (() => {
    if (groups.includes('Admin')) return AdminHomeDashboard;
    if (groups.includes('College')) return CollegeDashboard;
    if (groups.includes('House')) return HouseDashboard;
    // Staff roles land on the unified scoped Staff Dashboard.
    if (['Teacher', 'Principal', 'Supervisor', 'Beananti', 'CentreAdmin'].some(g => groups.includes(g))) return StaffDashboard;
    if (groups.includes('Parent')) return ParentDashboard;
    if (groups.includes('Guardian')) return ParentDashboard; // Guardians use the same dashboard as Parents
    return StudentDashboard;
  })();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
      <div className="no-print">
        <Navbar />
      </div>

      <main className="w-full">
        <Suspense fallback={<Spinner />}>
          <Routes>
            <Route
              path="/"
              element={user ? <Dashboard /> : <LandingPage />}
            />

            {/* Staff scoped dashboard (Teacher/Principal/Supervisor/Bean an Tí/Centre Admin) */}
            <Route
              path="/staff"
              element={
                <RequireAuth requiredGroups={['Teacher', 'Principal', 'Supervisor', 'Beananti', 'CentreAdmin', 'Admin']}>
                  <StaffDashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/staff/profile"
              element={
                <RequireAuth requiredGroups={['Teacher', 'Principal', 'Supervisor', 'Beananti', 'CentreAdmin', 'Admin']}>
                  <StaffProfilePage />
                </RequireAuth>
              }
            />

            <Route
              path="/admin"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminHomeDashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/staff-roles"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <StaffRolesPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/access-requests"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AccessRequestsReviewPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/courses"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminDashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/forms"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminFormsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/email"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminEmailPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/email-diagnostics"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminEmailDiagnosticPage />
                </RequireAuth>
              }
            />


            <Route path="/courses" element={<CourseList />} />
            <Route path="/information" element={<InformationPage />} />
            <Route path="/profile" element={<StudentProfilePage />} />
            <Route
              path="/guardian/profile"
              element={
                <RequireAuth requiredGroups={['Guardian', 'Parent', 'Admin']}>
                  <GuardianProfilePage />
                </RequireAuth>
              }
            />
            <Route
              path="/guardian/student/:studentId"
              element={
                <RequireAuth requiredGroups={['Guardian', 'Parent', 'Admin']}>
                  <GuardianStudentProfilePage />
                </RequireAuth>
              }
            />
            <Route path="/courses/:id" element={<CourseDetail />} />
            <Route 
              path="/courses/:courseId/details" 
              element={
                <RequireAuth>
                  <StudentCourseDetailsPage />
                </RequireAuth>
              }
            />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route 
              path="/student/bookings/:bookingId/edit-form" 
              element={
                <RequireAuth requiredGroups={['Student', 'Guardian', 'Parent', 'Admin']}>
                  <EditBookingFormPage />
                </RequireAuth>
              }
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/waitlist/accept/:token" element={<WaitlistAcceptPage />} />
            <Route path="/waitlist/accept/:token/" element={<WaitlistAcceptPage />} />
            <Route
              path="/admin/payment-types"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminPaymentTypesPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/payments"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminPaymentsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/globalpay"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminGlobalPayPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/payment-fix"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminPaymentFixPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/cognito-users"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminCognitoUsersPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/guardian-interest"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminGuardianInterestPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/bookings"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminBookingsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/search"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminSearchPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/view-as"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <UserViewAsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/issues"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <IssueListPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/issues/new"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <IssueSubmissionPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/issues/:issueId/edit"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <IssueEditPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/bookings/:bookingId/edit-form"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <EditBookingFormPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/bookings/:bookingId"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <BookingDetailsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/bookings/:bookingId/printable-record"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <PrintableStudentRecordPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/audit-bookings"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AuditCleanBookingsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/audit-log"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminAuditLogPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/user-permissions"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminPermissionsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminReportsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/students"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <AdminStudentsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/students/:studentId"
              element={
                <RequireAuth requiredGroups={['Admin']}>
                  <StudentDetailsPage />
                </RequireAuth>
              }
            />
            <Route path="/admin/houses" element={
              <RequireAuth requiredGroups={['Admin']}>
                <AdminHousesPage />
              </RequireAuth>
            } />
            <Route path="/admin/staff/teachers" element={
              <RequireAuth requiredGroups={['Admin']}>
                <TeacherAssignmentsPage />
              </RequireAuth>
            } />
            <Route path="/admin/staff/principals" element={
              <RequireAuth requiredGroups={['Admin']}>
                <PrincipalAssignmentsPage />
              </RequireAuth>
            } />
            <Route path="/admin/course-reports" element={
              <RequireAuth requiredGroups={['Admin']}>
                <ComprehensiveCourseReportPage />
              </RequireAuth>
            } />
            <Route path="/admin/course-house" element={
              <RequireAuth requiredGroups={['Admin']}>
                <AdminCourseHousesPage />
              </RequireAuth>
            } />
            <Route path="/admin/course-management" element={
              <RequireAuth requiredGroups={['Admin']}>
                <CourseManagementPage />
              </RequireAuth>
            } />
            <Route path="/admin/course-management/:courseId" element={
              <RequireAuth requiredGroups={['Admin']}>
                <CourseManagementPage />
              </RequireAuth>
            } />
            <Route path="/admin/course-details/:courseId" element={
              <RequireAuth requiredGroups={['Admin']}>
                <CourseDetailsManagementPage />
              </RequireAuth>
            } />
            <Route path="/admin/course-book/:courseId" element={
              <RequireAuth requiredGroups={['Admin']}>
                <CourseBookPage />
              </RequireAuth>
            } />
            <Route path="/admin/form-management" element={
              <RequireAuth requiredGroups={['Admin']}>
                <AdminFormManagementPage />
              </RequireAuth>
            } />
            <Route path="/admin/housing-management" element={
              <RequireAuth requiredGroups={['Admin']}>
                <HousingDashboard />
              </RequireAuth>
            } />
            <Route path="/admin/waitlist" element={
              <RequireAuth requiredGroups={['Admin']}>
                <AdminWaitlistPage />
              </RequireAuth>
            } />
            <Route path="/admin/refunds" element={
              <RequireAuth requiredGroups={['Admin']}>
                <AdminRefundPage />
              </RequireAuth>
            } />
            <Route path="/admin/courses/:courseId/slot-status" element={
              <RequireAuth requiredGroups={['Admin']}>
                <AdminCourseSlotStatusPage />
              </RequireAuth>
            } />
            <Route path="/admin/slot-monitoring" element={
              <RequireAuth requiredGroups={['Admin']}>
                <AdminSlotMonitoringPage />
              </RequireAuth>
            } />
            <Route path="/admin/slots" element={
              <RequireAuth requiredGroups={['Admin']}>
                <AdminSlotQueuePage />
              </RequireAuth>
            } />
            
            <Route path="/admin/data" element={
              <RequireAuth requiredGroups={['Admin']}>
                <DataManagementPage />
              </RequireAuth>
            } />
            
            <Route path="/admin/management" element={
              <RequireAuth requiredGroups={['Admin']}>
                <AdminManagementPage />
              </RequireAuth>
            } />
            
            <Route path="/admin/financial-dashboard" element={
              <RequireAuth requiredGroups={['Admin']}>
                <FinancialDashboard />
              </RequireAuth>
            } />

            <Route path="/admin/financial-diagnostics" element={
              <RequireAuth requiredGroups={['Admin']}>
                <FinancialDiagnosticsPage />
              </RequireAuth>
            } />

            <Route path="/admin/global-payments" element={
              <Navigate to="/admin/globalpay" replace />
            } />

            <Route
              path="/unauthorized"
              element={
                <div className="max-w-md mx-auto mt-20 bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 text-center">
                  <h2 className="text-2xl font-semibold text-red-600 mb-4">Unauthorized</h2>
                  <p className="mb-6">You don't have permission to view this page.</p>
                  <button
                    onClick={() => window.history.back()}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition"
                  >
                    Go Back
                  </button>
                </div>
              }
            />
          </Routes>
        </Suspense>
      </main>

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
}
